import React from 'react';
import PropTypes from 'prop-types';
import { IconLocation, IconPlus } from '@americanexpress/dls-icons';
import { useRouteParams } from '@/context/routeParamsContext';
import { useModal } from '@/context/modalContext';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  useLoungeById,
  useLoungeDirections,
  useLoungeTerminal,
  useLoungeMapImages,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { modalTypes } from '@/components/common/modals/constants';
import { isCenturionLounge } from '@/helpers/utils';
import {
  TclMigrationFeatures,
  useTCLFeatureFlags,
} from '@/hooks/featureFlagHooks';

const LoungeMapButton = ({ loungeId }) => {
  const { setModalName } = useModal();
  const { smallMapImage } = useLoungeMapImages(loungeId);
  const {
    centurionNetwork: { loungeMap },
  } = useCommonContent();

  const handleOpenMapModal = () => {
    setModalName(modalTypes.LOCATION_MAP_MODAL);
  };

  if (!smallMapImage) {
    return null;
  }

  return (
    <button
      type="button"
      className="tw-h-11 tw-w-11 tw-min-w-11 tw-shrink-0 tw-cursor-pointer tw-overflow-hidden"
      onClick={handleOpenMapModal}
      aria-label={loungeMap?.modalButtonAriaLabel}
    >
      <div className="tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white">
        <img
          src={smallMapImage}
          alt={loungeMap?.smallImageAltText}
          className="tw-h-full tw-w-full tw-scale-110 tw-object-cover"
        />
        <span className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-tl-lg tw-border-l tw-border-t tw-border-gray-300 tw-bg-white tw-shadow-sm">
          <IconPlus size="xs" className="tw-text-gray-700" />
        </span>
      </div>
    </button>
  );
};

LoungeMapButton.propTypes = {
  loungeId: PropTypes.string.isRequired,
};

const LegacyLoungeLocationDetails = () => {
  const { loungeId } = useRouteParams();
  const terminal = useLoungeTerminal(loungeId);
  const directions = useLoungeDirections(loungeId);
  const { lounges } = useCommonContent();

  return (
    <div className="margin-3-b pad-3-b tw-border-b tw-border-gray-300">
      <h2 className="margin-1-b tw-heading-xs-bold tw-text-gray-900">
        {lounges?.location?.header}
      </h2>
      <div>{terminal}</div>
      {directions && <div>{directions}</div>}
    </div>
  );
};

const RestyledLoungeLocationDetails = () => {
  const { loungeId } = useRouteParams();
  const lounge = useLoungeById(loungeId);
  const terminal = useLoungeTerminal(loungeId);
  const directions = useLoungeDirections(loungeId);

  return (
    <div className="margin-3-b pad-3-b tw-border-b tw-border-gray-300">
      <div className="tw-flex tw-items-center tw-gap-3">
        <IconLocation className="tw-shrink-0" size="md" />
        <div className="tw-flex-1">
          <strong>{terminal}</strong>
          {directions && <span> &bull; {directions}</span>}
        </div>
        {isCenturionLounge(lounge) && <LoungeMapButton loungeId={loungeId} />}
      </div>
    </div>
  );
};

const LoungeLocationDetails = () => {
  const isLocationMapEnabled = useTCLFeatureFlags(
    TclMigrationFeatures.LOCATION_MAP
  );

  return isLocationMapEnabled ? (
    <RestyledLoungeLocationDetails />
  ) : (
    <LegacyLoungeLocationDetails />
  );
};

export default LoungeLocationDetails;
